import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import {
  ErrorStrings,
  Genders,
  UserTitles,
  ValidationPatterns,
} from 'src/app/constants/form.constants';
import { Alert } from 'src/app/interfaces/alert';
import {
  RedirectionParams,
  RedirectionService,
} from 'src/app/services/redirection/redirection.service';
import { OwnerDetail } from 'src/app/interfaces/Account/ownerDetail';
import { UserService } from 'src/app/services/user/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UpdateOwnerDetailDTO } from 'src/app/interfaces/Account/updateOwnerDetailDTO';
import {
  User,
  UserAddress,
  UserAddressType,
  UserTelephone,
  UserTelephoneType,
} from 'src/app/interfaces/user';
import { HttpErrorResponse } from '@angular/common/http';
import {
  NgbCalendar,
  NgbDate,
  NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { CommonUtilService } from 'src/app/services/common/common-util.service';
import { OwnerAddress } from 'src/app/interfaces/Account/ownerAddress';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AddressService } from 'src/app/services/address/address.service';
import { AddressPAF } from 'src/app/services/address-finder/address-finder.service';
import {
  RegistrationProgressData,
  RegistrationProgressService,
} from 'src/app/services/registration-progress/registration-progress.service';
import { PetClaimingInfo, PetService } from 'src/app/services/pet/pet.service';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-owner-details',
  templateUrl: './owner-details.component.html',
  styleUrls: ['./owner-details.component.scss'],
})
export class OwnerDetailsComponent implements OnInit, OnDestroy {
  public loading = false;
  public alert: Alert = null;
  public errorStrings = ErrorStrings;
  public validationPatterns = ValidationPatterns;
  public canSkip: boolean = false;
  public isBrowser: boolean;

  public keeperDetailModel: OwnerDetail = {
    title: null,
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    gender: null,
    phoneNumber: '',
    secondPhoneNumber: '',
  };

  public addressModel: OwnerAddress = {
    addressLine1: '',
    addressLine2: '',
    townPerCity: '',
    postcode: '',
  };
  
  private petClaimModel: PetClaimingInfo = {
    chipNumber: '',
    vetCode: '',
    vetCodeSkipped: true,
  };

  public postcodeCheck = null;
  public selectedAddress = null;
  public addressesSelectList = [];
  public isPostcodeInvalid = false;

  public UserTitles = UserTitles;
  public dateOfBirthValue: NgbDateStruct;
  public today: NgbDate;
  public minDate: NgbDateStruct = { year: 1900, month: 1, day: 1 };
  public Genders = Genders;

  private _postcodeCheckChanged: Subject<string> = new Subject<string>();
  public _user: User;
  private _existingTelephones: UserTelephone[];
  private _redirectParams?: RedirectionParams;
  private _mainAddress: UserAddress = null;
  private _subs: Subscription[] = [];
  private registrationProgress: RegistrationProgressData;

  constructor(
    private _userService: UserService,
    private _redirectionService: RedirectionService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _calendar: NgbCalendar,
    private _commonUtilService: CommonUtilService,
    private _addressService: AddressService,
    private registrationProgressService: RegistrationProgressService,
    private petService: PetService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this._user = this._activatedRoute.snapshot.data.user as User;
    this._mainAddress = this._user.addresses.filter(
      (address) => address.addressType === UserAddressType.Main
    )[0];
    this.registrationProgress =
      registrationProgressService.getRegistrationProgressData(this._user);
    this.canSkip = this.registrationProgress.isFullNameRegistrationCompleted;
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.initSubs();
    this.initForm();

    // Setting redirection to be used after the owner data was updated
    this._redirectParams = this._redirectionService.getParams(
      this._activatedRoute.snapshot.queryParams.r
    );
  }

  ngOnDestroy() {
    this._subs.forEach((s) => s.unsubscribe());
  }

  public onNextClick() {
    this.navigateToNextStep();
  }

  public updateOwnerDetailsAndAddresses() {
    this.closeAlert();
    this.loading = true;

    this.keeperDetailModel.dateOfBirth = this.dateOfBirthValue
      ? this._commonUtilService.createDateObject(this.dateOfBirthValue)
      : null;
    const ownerData: UpdateOwnerDetailDTO = {
      title: this.keeperDetailModel.title,
      firstName: this.keeperDetailModel.firstName,
      lastName: this.keeperDetailModel.lastName,
      dateOfBirth: this.keeperDetailModel.dateOfBirth,
      gender: this.keeperDetailModel.gender,
      telephoneNumbers: this.getPhoneNumberList(),
    };

    const ownerAddressDTO = [
      {
        line1: this.addressModel.addressLine1,
        line2: this.addressModel.addressLine2,
        town: this.addressModel.townPerCity,
        county: this._mainAddress?.county ? this._mainAddress.county : null,
        postcode: this.addressModel.postcode,
        addressType: UserAddressType.Main,
        temporary: this._mainAddress?.temporary
          ? this._mainAddress.temporary
          : null,
      },
    ];

    this._userService
      .updateOwnerDetailsAndAddresses(ownerData, ownerAddressDTO)
      .subscribe(
        (response) => {
          this.getExistingTelephones();

          this.alert = {
            type: 'success',
            message:
              "Your detail information was updated. You'll get redirected in a few seconds!",
          };

          setTimeout(() => {
            if (this._redirectParams) {
              this._router.navigate(this._redirectParams.l, {
                queryParams: this._redirectParams.q,
                fragment: this._redirectParams.f,
              });
            } else {
              this.navigateToNextStep();
            }
          }, 1000);
        },
        (err) => {
          if (err instanceof HttpErrorResponse && err.status === 400) {
            this.alert = {
              type: 'danger',
              message: err.error?.error_message,
            };
          } else {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        }
      )
      .add(() => {
        this.loading = false;
      });
  }

  private navigateToNextStep() {
    if (this._user.pets.length === 0) {
      if (this.isBrowser && localStorage.getItem('enteredChipNumber')) {
        this.petClaimModel.chipNumber = atob(localStorage.getItem('enteredChipNumber'));
        this.petService
        .claimPet(this.petClaimModel)
        .subscribe(
          (pet) => {
            localStorage.removeItem('enteredChipNumber');
            this._router.navigate(['/my-pet', pet.id, 'confirm']);
          },
          (err) => {console.error(err)}
        )
        return;
      }
      // The user has not claimed any pets before
      this._router.navigate(['/claim-pet'], {
        queryParams: { r: 'dashboard' },
      });
      return;
    }

    // Navigate the user to the first pet
    const sortedPets = this._userService.getPetsForUserOrderedById(this._user);
    this._router.navigate(['/my-pet', sortedPets[0].id, 'confirm']);
  }

  public closeAlert() {
    this.alert = null;
  }

  public onPostCodeCheckChanges(inputEvent) {
    this._postcodeCheckChanged.next(inputEvent.target.value);
  }

  focusOnSelect() {
    if (!this.isBrowser) return;
    const selectElement = document.getElementById('selectedAddress') as HTMLSelectElement;
    if (selectElement && this.addressesSelectList.length > 0) {
      selectElement.focus();
    }
  }

  public getAddressLabel(addressOption: AddressPAF) {
    return this.createAddressLine1(addressOption);
  }

  public onSelectAddressChanged(selectedAddress: AddressPAF) {
    this.addressModel = {
      addressLine1: this.createAddressLine1(selectedAddress),
      addressLine2: this.addressModel.addressLine2,
      townPerCity: selectedAddress.town,
      postcode: selectedAddress.postcode,
    };
  }

  private createAddressLine1(selectedAddress: AddressPAF): string {
    let result = `${selectedAddress.thoroughfare}`;
    if (
      selectedAddress.buildingNumber &&
      selectedAddress.buildingNumber.length > 0
    ) {
      result = `${selectedAddress.buildingNumber}, ` + result;
    }

    if (
      selectedAddress.buildingName &&
      selectedAddress.buildingName.length > 0
    ) {
      result = `${selectedAddress.buildingName}, ` + result;
    }

    if (
      selectedAddress.subBuildingName &&
      selectedAddress.subBuildingName.length > 0
    ) {
      result = `${selectedAddress.subBuildingName}, ` + result;
    }

    if (
      selectedAddress.organisationName &&
      selectedAddress.organisationName.length > 0
    ) {
      result = `${selectedAddress.organisationName}, ` + result;
    }

    return result;
  }

  private initSubs() {
    this._subs.push(
      this._postcodeCheckChanged
        .pipe(debounceTime(1000))
        .subscribe((postcode: string) => {
          this.isPostcodeInvalid = false;
          this.verifyPostcodeAndGetAddressList(postcode);
        })
    );
  }

  private verifyPostcodeAndGetAddressList(postCode: string) {
    this._addressService
      .checkPostcodeValidity(postCode)
      .subscribe((isValidPostcode: boolean) => {
        this.addressesSelectList = [];
        if (isValidPostcode) {
          this._addressService
            .getAddressMatches(postCode)
            .subscribe((addresses: AddressPAF[]) => {
              this.addressesSelectList = addresses;
            });
        } else {
          this.isPostcodeInvalid = true;
        }
      });
  }

  private initForm() {
    this.today = this._calendar.getToday();
    this._existingTelephones = this.getExistingTelephones();
    this.keeperDetailModel = {
      title: this._user.title,
      firstName: this._user.name.firstName,
      lastName: this._user.name.lastName,
      gender: this._user.gender,
      phoneNumber: this._existingTelephones[0].number,
      secondPhoneNumber: this._existingTelephones[1].number,
    };

    if (this._mainAddress) {
      this.addressModel = {
        addressLine1: this._mainAddress.line1,
        addressLine2: this._mainAddress.line2,
        townPerCity: this._mainAddress.town,
        postcode: this._mainAddress.postcode,
      };
    }

    this.setUserDateOfBirth();
  }

  // Returns an array of type UserTelephone as empty objects or with the existing telephones retrieved from
  // the user object ordered by UserTelephoneType
  private getExistingTelephones(): UserTelephone[] {
    const existingTelephones: UserTelephone[] = [];
    existingTelephones.push(this.getTelephone(UserTelephoneType.Main));
    existingTelephones.push(this.getTelephone(UserTelephoneType.Second));
    return existingTelephones;
  }

  private setUserDateOfBirth() {
    if (this._user.dateOfBirth) {
      this.dateOfBirthValue = NgbDate.from({
        day: new Date(this._user.dateOfBirth).getDate(),
        month: new Date(this._user.dateOfBirth).getMonth() + 1,
        year: new Date(this._user.dateOfBirth).getFullYear(),
      });
    }
  }

  private getTelephone(type: UserTelephoneType): UserTelephone {
    const telephone = this._user.contact.telephone.filter(
      (phone) => phone.numberType === type
    )[0];
    if (telephone) {
      return {
        number: telephone.number,
        numberType: telephone.numberType,
      };
    }
    return {
      number: '',
      numberType: type,
    };
  }

  private getPhoneNumberList() {
    // If the telephone number got updated
    const updatedTelephones: UserTelephone[] = [
      {
        number: this.keeperDetailModel.phoneNumber
          ? this.keeperDetailModel.phoneNumber
          : '',
        numberType: UserTelephoneType.Main,
      },
      {
        number: this.keeperDetailModel.secondPhoneNumber
          ? this.keeperDetailModel.secondPhoneNumber
          : '',
        numberType: UserTelephoneType.Second,
      },
    ];

    const newTelephoneNumbers: UserTelephone[] = [];
    updatedTelephones.forEach((telephone, i) => {
      // If the telephone number got updated
      if (!!telephone.number) {
        newTelephoneNumbers.push(telephone);
      }
      // If the telephone number was already set and didn't get updated
      if (this._existingTelephones[i].number && telephone.number === '') {
        newTelephoneNumbers.push(this._existingTelephones[i]);
      }
    });

    return newTelephoneNumbers;
  }

  public getProgressData() {
    const result: RegistrationProgressData = {
      isFullNameRegistrationCompleted: false,
      isPetChipNumberRegistrationCompleted: false,
      isMembershipSelectionCompleted: false,
      isWelcomePackSelectCompleted: false,
    };
    return result;
  }
}
